import React from 'react';

import * as Draft from 'draft-js';

import { SYMBOLS_LIMIT } from 'constants/texts';

import { useEditor } from './useEditor';
import * as t from './Wysiwyg.types';

export const WysiwygContext = React.createContext({} as t.Context);

const transformPlaceholderObjectToText = (object: any): string => {
  if (typeof object === 'object' && 'text' in object) {
    return object.text;
  }
  if (typeof object === 'object' && 'raw' in object) {
    return Draft.convertFromRaw(object.raw).getPlainText();
  }

  return object;
};

export const useWysiwygContext = () => {
  const context = React.useContext(WysiwygContext);
  if (!context) {
    throw new Error('useWysiwygContext must be used within a WysiwygProvider');
  }
  return context;
};

const WysiwygProvider = (props: t.ProviderProps) => {
  const {
    type = 'primary',
    isReadOnly = false,
    isDisabled = false,
    limit = SYMBOLS_LIMIT,
    hideLimit = false,
    placeholder = '',
    focusedPlaceholder = '',
  } = props;

  const innerProps = {
    ...props,
    type,
    placeholder: transformPlaceholderObjectToText(placeholder),
    focusedPlaceholder: transformPlaceholderObjectToText(focusedPlaceholder),
    isReadOnly,
    isDisabled,
    limit,
    hideLimit,
  };
  const editor = useEditor(innerProps);

  return (
    <WysiwygContext.Provider
      value={{
        props: innerProps,
        editor,
      }}
    >
      {props.children}
    </WysiwygContext.Provider>
  );
};

export const withWysiwyg = (WrappedComponent: React.ComponentType) => {
  return (props: t.Props) => {
    return (
      <WysiwygProvider {...props}>
        <WrappedComponent />
      </WysiwygProvider>
    );
  };
};
